import { Label, TextInput, Button } from '@consigli/facade';
import { useMyUser } from '@consigli/hooks';
import { isSharedOracleProject } from '@consigli/types';
import { type FC, useCallback, useMemo, useState } from 'react';
import { UseFormRegister, FieldErrors, Control, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LegalWarning } from '@/components/legal-warning';
import { Breadcrumb } from '@/features/breadcrumb/breadcrumb';
import { Route } from '@/routes';
import { ProjectInputs } from '@/util/types';

import { ConstructionTypesField } from './fields/construction-types-field';
import { OracleProjectField } from './fields/oracle-project-field';
import { ProjectCountryField } from './fields/project-country-field';

type ProjectProps = {
  register: UseFormRegister<ProjectInputs>;
  errors: FieldErrors<ProjectInputs>;
  control: Control<ProjectInputs>;
  getValues: UseFormReturn<ProjectInputs>['getValues'];
  setValue: UseFormReturn<ProjectInputs>['setValue'];
  isLoading: boolean;
  nameClash?: { original: string; suggested: string } | null;
  onDismissNameClash?: () => void;
};

export const ProjectDetails: FC<ProjectProps> = ({
  register,
  errors,
  control,
  // getValues,
  setValue,
  isLoading,
  nameClash,
  onDismissNameClash,
}) => {
  const { user } = useMyUser();
  const { t } = useTranslation();

  const isAecomUser = useMemo(
    () => user.email?.toLowerCase().endsWith('@aecom.com') ?? false,
    [user.email],
  );

  const selectedProjectNumber = useWatch({ control, name: 'oracleProjectNumber' });
  const [clientName, setClientName] = useState<string | null>(null);
  const [nameOverride, setNameOverride] = useState(false);
  const [isSharedProject, setIsSharedProject] = useState(false);

  // Indirect and Bid & Proposal projects are shared by many users, so the same
  // Oracle name collides. They get a fully free-form, editable name that the
  // user types themselves. Direct projects stay read-only.
  const isNameReadOnly = isAecomUser && !isSharedProject && (import.meta.env.PROD || !nameOverride);

  const nameRegistration = register('name', {
    required: `${t('project-creation.project-name-required')}*`,
  });

  const handleProjectChange = useCallback(
    (
      projectNumber: string | null,
      projectName: string | null,
      customerName: string | null,
      projectTypeClassCode: string | null,
    ) => {
      const shared = isSharedOracleProject(projectTypeClassCode);
      setIsSharedProject(shared);
      setClientName(customerName);
      setValue('oracleProjectTypeClassCode', projectTypeClassCode ?? undefined, {
        shouldValidate: false,
      });
      // Shared projects use a fully free-form name and start blank, so the
      // user names them themselves. Skip validation on prefill to avoid showing
      // a premature "required" error on the empty field. Direct projects keep
      // the exact Oracle name.
      let prefilledName = projectName ?? '';
      let shouldValidateName = Boolean(projectName);
      if (shared) {
        prefilledName = '';
        shouldValidateName = false;
      }
      setValue('name', prefilledName, { shouldValidate: shouldValidateName });
      if (!projectNumber) {
        setIsSharedProject(false);
        setValue('oracleProjectTypeClassCode', undefined, { shouldValidate: false });
      }
    },
    [setValue],
  );

  return (
    <>
      <Breadcrumb label={t('project-creation.new-project')} to={`/${Route.NEW_PROJECT}`} />
      <div className="flex flex-col gap-4 mt-1">
        {isAecomUser && (
          <LegalWarning className="bg-day-light-3 border border-day-light-1 rounded-lg p-4" />
        )}

        {isAecomUser && (
          <OracleProjectField control={control} onProjectChange={handleProjectChange} />
        )}

        {isAecomUser && (
          <Label text={t('project-creation.client-name-label')}>
            <TextInput
              value={
                selectedProjectNumber
                  ? clientName?.trim()
                    ? clientName
                    : t('project-creation.client-name-not-found')
                  : ''
              }
              readOnly
              tabIndex={-1}
              className="border border-day-light-2 bg-day-light-3 text-day-neutral-subtle cursor-default"
            />
          </Label>
        )}
        <Label text={t('project-creation.project-name-label')}>
          <TextInput
            {...nameRegistration}
            onChange={(event) => {
              onDismissNameClash?.();
              return nameRegistration.onChange(event);
            }}
            className={
              isNameReadOnly
                ? 'border border-day-light-2 bg-day-light-3 text-day-neutral-subtle cursor-default'
                : 'border border-day-light-2'
            }
            readOnly={isNameReadOnly}
          />
          {isAecomUser && (
            <div className="flex items-center gap-2 mt-1">
              <p className="text-xs text-day-neutral-subtle">
                {t(
                  isSharedProject
                    ? 'project-creation.project-name-shared-hint'
                    : 'project-creation.project-name-auto-filled',
                )}
              </p>
              {!isSharedProject && (!import.meta.env.PROD || user.isSuperuser) && (
                <label className="flex items-center gap-1 text-xs text-day-neutral-subtle cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={nameOverride}
                    onChange={(e) => setNameOverride(e.target.checked)}
                    className="cursor-pointer"
                  />
                  {t('project-creation.project-name-override')}
                </label>
              )}
            </div>
          )}
          {errors.name && (
            <div className={'text-sm my-1 text-error-dark'}>{errors.name.message}</div>
          )}
          {nameClash && (
            <div className="text-xs my-1 text-day-neutral-dark">
              {t('project-creation.project-name-suggested-unique', {
                original: nameClash.original,
                suggested: nameClash.suggested,
              })}
            </div>
          )}
        </Label>
        {!isAecomUser && (
          <Label text={t('project-creation.project-address-label')}>
            <TextInput {...register('address')} className="border border-day-light-2" />
          </Label>
        )}
        <ProjectCountryField control={control} />
        <ConstructionTypesField control={control} errors={errors} />
        {/* <Specifications
          control={control}
          errors={errors}
          initialValue={getValues('specifications')}
        /> */}

        <div className="my-4">
          <Button primary rounded className="w-full" type="submit" disabled={isLoading}>
            {t('project-creation.create-project')}
          </Button>
        </div>
      </div>
    </>
  );
};
